import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Progress } from "./ui/progress";
import { Sparkles, ArrowRight, RotateCcw, GraduationCap, Briefcase, Crown } from "lucide-react";

// Quiz simplifié — basé uniquement sur les 3 programmes proposés sur le site :
// BBA (Bac+1 à Bac+3) · MBA (Bac+4 à Bac+5) · MBA Executive (cadres en activité)
const QUESTIONS = [
  {
    q: "Quel est votre niveau d'études actuel ?",
    options: [
      { label: "Bac (terminale ou post-bac récent)", scores: { bba: 3 } },
      { label: "Bac+2 ou Bac+3", scores: { mba: 3 } },
      { label: "Bac+4 ou Bac+5", scores: { mba: 2, emba: 1 } },
      { label: "Cadre en activité depuis 5 ans ou plus", scores: { emba: 3 } },
    ],
  },
  {
    q: "Quel est votre objectif principal ?",
    options: [
      { label: "Acquérir des bases solides en management", scores: { bba: 3 } },
      { label: "Devenir cadre stratégique / dirigeant", scores: { mba: 3, emba: 1 } },
      { label: "Booster ma carrière sans interrompre mon travail", scores: { emba: 3 } },
      { label: "Me spécialiser après un Bachelor", scores: { mba: 3 } },
    ],
  },
  {
    q: "Quel format d'études vous correspond ?",
    options: [
      { label: "Formation initiale à temps plein", scores: { bba: 2, mba: 2 } },
      { label: "Alternance école / entreprise", scores: { bba: 2, mba: 2 } },
      { label: "E-learning à mon rythme", scores: { bba: 1, mba: 1, emba: 1 } },
      { label: "Week-ends et séminaires (en parallèle de mon poste)", scores: { emba: 3 } },
    ],
  },
  {
    q: "Combien de temps souhaitez-vous y consacrer ?",
    options: [
      { label: "3 ans (du Bac jusqu'au Bac+3)", scores: { bba: 3 } },
      { label: "2 ans pour atteindre un Bac+5", scores: { mba: 3 } },
      { label: "1 an pour me perfectionner", scores: { mba: 1, emba: 2 } },
      { label: "Quelques mois par an, en alternance avec mon job", scores: { emba: 3 } },
    ],
  },
];

const RESULTS = {
  bba: {
    title: "BBA — Bachelor of Business Administration",
    eyebrow: "Post-bac · 3 ans · Bac+3",
    icon: GraduationCap,
    reason:
      "Le Bachelor of Business Administration est un diplôme international de premier cycle en administration des entreprises (management, finance, marketing, RH, stratégie). Il vous prépare à devenir opérationnel en 3 ans, avec une spécialisation en 3ème année parmi 7 options.",
    perks: [
      "Accessible Post-bac (Général, Techno, Pro)",
      "Initial · Alternance · E-learning",
      "7 spécialisations en BBA3",
      "Tremplin vers le MBA pour un Bac+5",
    ],
    cta: "/formations/bba",
  },
  mba: {
    title: "MBA — Master of Business Administration",
    eyebrow: "Bac+4 à Bac+5 · 2 ans",
    icon: Briefcase,
    reason:
      "Le MBA de l'ECL forme les futurs cadres dirigeants à la vision stratégique et au leadership. Une formation 70/30 (pratique/théorie), avec spécialisation en 2ème année et un fort accent international.",
    perks: [
      "Pédagogie 70/30 (70 % pratique)",
      "Spécialisation en MBA2 (Bac+5)",
      "Initial · Alternance · E-learning",
      "Réseau international de 7 campus",
    ],
    cta: "/formations/mba",
  },
  emba: {
    title: "MBA Executive",
    eyebrow: "Pour cadres en activité",
    icon: Crown,
    reason:
      "Le MBA Executive est conçu pour les cadres et dirigeants en activité souhaitant accélérer leur carrière sans interrompre leur poste. Format flexible compatible avec une activité professionnelle à temps plein.",
    perks: [
      "Compatible avec un poste à temps plein",
      "Format week-ends et séminaires",
      "Communauté de pairs (cadres expérimentés)",
      "Diplôme Bac+5 reconnu",
    ],
    cta: "/formations/mba-executive",
  },
};

const INITIAL_SCORES = { bba: 0, mba: 0, emba: 0 };

export const Quiz = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [scores, setScores] = useState(INITIAL_SCORES);
  const [done, setDone] = useState(false);
  const [answers, setAnswers] = useState([]);

  const onAnswer = async (opt) => {
    const newScores = { ...scores };
    Object.entries(opt.scores).forEach(([k, v]) => { newScores[k] = (newScores[k] || 0) + v; });
    const newAnswers = [...answers, opt.label];
    setScores(newScores);
    setAnswers(newAnswers);
    if (step + 1 < QUESTIONS.length) {
      setStep(step + 1);
    } else {
      setDone(true);
      try {
        const top = Object.entries(newScores).sort((a, b) => b[1] - a[1])[0];
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quiz`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ answers: newAnswers, recommended_program: top[0], score: top[1] }),
        });
      } catch (e) { /* no-op */ }
    }
  };

  const reset = () => {
    setStep(0);
    setScores(INITIAL_SCORES);
    setDone(false);
    setAnswers([]);
  };

  const top = Object.entries(scores).sort((a, b) => b[1] - a[1])[0];
  const totalMax = QUESTIONS.length * 3;
  const matchPct = top ? Math.min(100, Math.round((top[1] / totalMax) * 100) + 10) : 0;
  const result = top ? RESULTS[top[0]] : null;
  const progress = ((step) / QUESTIONS.length) * 100;
  const ResultIcon = result?.icon;

  return (
    <section id="quiz" data-testid="quiz-section" className="section-padding bg-[#FAF8F3] relative overflow-hidden border-y border-navy-100">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-red-500/5 blur-3xl rounded-full" />
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-navy-500/5 blur-3xl rounded-full" />
      </div>

      <div className="container-edge relative z-10">
        <div className="text-center mb-14">
          <div className="overline mb-4 text-red-500">Quiz d'orientation</div>
          <h2 className="font-display text-2xl sm:text-3xl md:text-5xl text-navy-700 leading-tight">
            Trouvez votre <em className="not-italic red-shine">formation</em>.
          </h2>
          <p className="text-[#555] mt-5 max-w-xl mx-auto text-[15px]">
            4 questions, moins d'une minute pour découvrir lequel de nos programmes vous correspond.
          </p>
        </div>

        <div className="max-w-2xl mx-auto bg-white border border-navy-100 p-8 md:p-12 shadow-[0_24px_60px_-30px_rgba(0,35,98,0.25)]">
          {!done ? (
            <>
              <div className="flex items-center justify-between mb-3 text-navy-500 text-xs uppercase tracking-[0.2em]">
                <span data-testid="quiz-step-indicator">Question {step + 1} / {QUESTIONS.length}</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} className="h-1 bg-navy-100 mb-10 [&>div]:bg-red-500" />

              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.4 }}
                >
                  <h3 className="font-display text-2xl md:text-3xl text-navy-700 mb-8 leading-snug" data-testid={`quiz-question-${step}`}>
                    {QUESTIONS[step].q}
                  </h3>
                  <div className="grid gap-3">
                    {QUESTIONS[step].options.map((opt, i) => (
                      <button
                        key={i}
                        onClick={() => onAnswer(opt)}
                        data-testid={`quiz-option-${step}-${i}`}
                        className="text-left px-6 py-4 border border-navy-100 text-navy-700 hover:border-navy-500 hover:bg-[#FAF8F3] transition-all group flex items-center justify-between"
                      >
                        <span className="text-[15px]">{opt.label}</span>
                        <ArrowRight className="h-4 w-4 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    ))}
                  </div>
                </motion.div>
              </AnimatePresence>
            </>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              data-testid="quiz-result"
            >
              <div className="text-center mb-6">
                {ResultIcon && (
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-red-500 to-red-600 text-white rounded-full mb-4 shadow-md">
                    <ResultIcon className="h-6 w-6" strokeWidth={1.6} />
                  </div>
                )}
                <div className="overline text-red-500 mb-2">Votre profil correspond à</div>
                <div className="font-display text-5xl md:text-6xl red-shine mb-3">{matchPct}%</div>
                <h3 className="font-display text-2xl md:text-3xl text-navy-700 mb-2 leading-tight">
                  {result?.title}
                </h3>
                <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-navy-500">
                  {result?.eyebrow}
                </div>
              </div>

              <p className="text-[#555] leading-[1.8] text-[14.5px] mb-6 text-center max-w-lg mx-auto">
                {result?.reason}
              </p>

              {result?.perks && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-8 max-w-lg mx-auto">
                  {result.perks.map((p, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2.5 bg-[#FAF8F3] px-3.5 py-2.5 border-l-2 border-red-500 text-[13px] text-navy-700"
                    >
                      <Sparkles className="h-3 w-3 text-red-500 shrink-0" />
                      {p}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap gap-3 justify-center">
                <button
                  className="btn-red"
                  onClick={() => navigate(result?.cta || "/formations")}
                  data-testid="quiz-result-program"
                >
                  Découvrir ce programme
                  <ArrowRight className="h-4 w-4" />
                </button>
                <button
                  className="btn-outline-navy"
                  onClick={() => navigate("/contact#candidater")}
                  data-testid="quiz-result-apply"
                >
                  Candidater
                </button>
                <button onClick={reset} className="text-[11px] uppercase tracking-[0.18em] font-semibold text-navy-500 hover:text-red-500 transition-colors flex items-center gap-1.5 px-3 py-2" data-testid="quiz-restart">
                  <RotateCcw className="h-3.5 w-3.5" />
                  Refaire le quiz
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Quiz;
